/**
 * Editable Data Table Component
 *
 * Provides spreadsheet-like editing capabilities for data table rows:
 * - Inline cell editing (double-click to edit)
 * - Row deletion
 * - Add new rows
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Trash2,
  Plus,
  Loader2,
  Save,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { dataTablesApi } from '@/lib/api';
import type { DataTable, DataTableRow, DataTableColumn } from '@/types/data-tables';

interface EditableDataTableProps {
  table: DataTable;
  rows: DataTableRow[];
  onRowsChange: () => void; // Callback to refresh rows after changes
  isOwner?: boolean;
}

interface EditingCell {
  rowId: string;
  columnName: string;
  value: any;
}

// Format date for display
function formatDate(dateString: string) {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// Format cell value for display
function formatCellValue(value: any, type: string): string {
  if (value === null || value === undefined) return '-';
  if (type === 'json') return JSON.stringify(value);
  if (type === 'boolean') return value ? 'Yes' : 'No';
  if (type === 'date' || type === 'datetime') {
    const date = new Date(value);
    return type === 'datetime'
      ? date.toLocaleString()
      : date.toLocaleDateString();
  }
  return String(value);
}

// Parse cell value based on column type
function parseCellValue(value: string, type: string): any {
  if (value === '' || value === '-') return null;

  switch (type.toLowerCase()) {
    case 'number':
    case 'integer':
    case 'decimal':
    case 'float':
      const num = parseFloat(value);
      return isNaN(num) ? null : num;
    case 'boolean':
      return value.toLowerCase() === 'true' || value.toLowerCase() === 'yes' || value === '1';
    case 'json':
    case 'object':
    case 'array':
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    default:
      return value;
  }
}

export function EditableDataTable({
  table,
  rows,
  onRowsChange,
  isOwner = true,
}: EditableDataTableProps) {
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [savingCell, setSavingCell] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<string | null>(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [addingRow, setAddingRow] = useState(false);
  const [newRowData, setNewRowData] = useState<Record<string, any>>({});
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when editing starts
  useEffect(() => {
    if (editingCell && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingCell]);

  // Get columns without system ID column
  const editableColumns = table.schema.columns.filter(
    (col) => col.name.toLowerCase() !== 'id'
  );

  // Handle cell double-click to start editing
  const handleCellDoubleClick = (rowId: string, column: DataTableColumn, currentValue: any) => {
    if (!isOwner) return;

    setEditingCell({
      rowId,
      columnName: column.name,
      value: currentValue !== null && currentValue !== undefined
        ? (column.type === 'json' ? JSON.stringify(currentValue) : String(currentValue))
        : '',
    });
  };

  // Handle cell value change
  const handleCellValueChange = (value: string) => {
    if (editingCell) {
      setEditingCell({ ...editingCell, value });
    }
  };

  // Save cell edit
  const handleSaveCell = async () => {
    if (!editingCell) return;

    const column = table.schema.columns.find(c => c.name === editingCell.columnName);
    if (!column) return;

    const row = rows.find(r => r.id === editingCell.rowId);
    if (!row) return;

    const parsedValue = parseCellValue(editingCell.value, column.type);
    const updatedData = { ...row.data, [editingCell.columnName]: parsedValue };

    try {
      setSavingCell(true);
      await dataTablesApi.updateRow(table.id, editingCell.rowId, updatedData);
      toast.success('Cell updated');
      setEditingCell(null);
      onRowsChange();
    } catch (error: any) {
      console.error('Failed to update cell:', error);
      toast.error(error.response?.data?.message || 'Failed to update cell');
    } finally {
      setSavingCell(false);
    }
  };

  // Cancel cell edit
  const handleCancelEdit = () => {
    setEditingCell(null);
  };

  // Handle key press in edit input
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveCell();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  // Handle single row delete
  const handleDeleteRow = async () => {
    if (!rowToDelete) return;

    try {
      setDeleting(true);
      await dataTablesApi.deleteRow(table.id, rowToDelete);
      toast.success('Row deleted');
      setDeleteDialogOpen(false);
      setRowToDelete(null);
      onRowsChange();
    } catch (error: any) {
      console.error('Failed to delete row:', error);
      toast.error(error.response?.data?.message || 'Failed to delete row');
    } finally {
      setDeleting(false);
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) return;

    try {
      setDeleting(true);
      // Delete rows one by one (API doesn't have bulk delete by IDs)
      const deletePromises = Array.from(selectedRows).map((rowId) =>
        dataTablesApi.deleteRow(table.id, rowId)
      );
      await Promise.all(deletePromises);
      toast.success(`${selectedRows.size} row(s) deleted`);
      setBulkDeleteDialogOpen(false);
      setSelectedRows(new Set());
      onRowsChange();
    } catch (error: any) {
      console.error('Failed to delete rows:', error);
      toast.error(error.response?.data?.message || 'Failed to delete rows');
    } finally {
      setDeleting(false);
    }
  };

  // Toggle row selection
  const toggleRowSelection = (rowId: string) => {
    const newSelection = new Set(selectedRows);
    if (newSelection.has(rowId)) {
      newSelection.delete(rowId);
    } else {
      newSelection.add(rowId);
    }
    setSelectedRows(newSelection);
  };

  // Toggle all rows selection
  const toggleAllRows = () => {
    if (selectedRows.size === rows.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(rows.map(r => r.id)));
    }
  };

  // Start adding a new row
  const handleStartAddRow = () => {
    const initialData: Record<string, any> = {};
    editableColumns.forEach((col) => {
      initialData[col.name] = col.default ?? '';
    });
    setNewRowData(initialData);
    setAddingRow(true);
  };

  // Cancel adding row
  const handleCancelAddRow = () => {
    setAddingRow(false);
    setNewRowData({});
  };

  // Save new row
  const handleSaveNewRow = async () => {
    try {
      // Parse values based on column types
      const parsedData: Record<string, any> = {};
      editableColumns.forEach((col) => {
        const rawValue = newRowData[col.name];
        if (rawValue !== undefined && rawValue !== '') {
          parsedData[col.name] = parseCellValue(String(rawValue), col.type);
        }
      });

      // Check required fields
      const missingRequired = editableColumns
        .filter((col) => col.required && !parsedData[col.name])
        .map((col) => col.name);

      if (missingRequired.length > 0) {
        toast.error(`Required fields missing: ${missingRequired.join(', ')}`);
        return;
      }

      await dataTablesApi.insertRow(table.id, parsedData);
      toast.success('Row added');
      setAddingRow(false);
      setNewRowData({});
      onRowsChange();
    } catch (error: any) {
      console.error('Failed to add row:', error);
      toast.error(error.response?.data?.message || 'Failed to add row');
    }
  };

  // Render cell content
  const renderCell = (row: DataTableRow, column: DataTableColumn) => {
    const value = row.data[column.name];
    const isEditing = editingCell?.rowId === row.id && editingCell?.columnName === column.name;

    if (isEditing) {
      return (
        <div className="flex items-center gap-1">
          <Input
            ref={inputRef}
            value={editingCell.value}
            onChange={(e) => handleCellValueChange(e.target.value)}
            onKeyDown={handleKeyDown}
            className="h-7 text-sm"
            disabled={savingCell}
          />
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6"
            onClick={handleSaveCell}
            disabled={savingCell}
          >
            {savingCell ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Save className="h-3 w-3" />
            )}
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6"
            onClick={handleCancelEdit}
            disabled={savingCell}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      );
    }

    return (
      <div
        className={`truncate ${isOwner ? 'cursor-pointer hover:bg-muted/50 px-1 -mx-1 rounded' : ''}`}
        onDoubleClick={() => handleCellDoubleClick(row.id, column, value)}
        title={isOwner ? 'Double-click to edit' : undefined}
      >
        {formatCellValue(value, column.type)}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Toolbar */}
      {isOwner && (
        <div className="flex-shrink-0 flex items-center justify-between p-2 border-b bg-muted/30">
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleStartAddRow} disabled={addingRow}>
              <Plus className="h-4 w-4 mr-1" />
              Add Row
            </Button>
            {selectedRows.size > 0 && (
              <Button
                size="sm"
                variant="destructive"
                onClick={() => setBulkDeleteDialogOpen(true)}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete ({selectedRows.size})
              </Button>
            )}
          </div>
          <div className="text-sm text-muted-foreground">
            {isOwner && 'Double-click a cell to edit'}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 min-h-0 overflow-auto">
        <Table className="min-w-max">
          <TableHeader>
            <TableRow>
              {isOwner && (
                <TableHead className="w-[40px]">
                  <Checkbox
                    checked={rows.length > 0 && selectedRows.size === rows.length}
                    onCheckedChange={toggleAllRows}
                  />
                </TableHead>
              )}
              <TableHead className="w-[100px]">ID</TableHead>
              {editableColumns.map((col) => (
                <TableHead key={col.name} className="min-w-[120px]">
                  {col.name}
                  {col.required && <span className="text-destructive ml-1">*</span>}
                  <span className="ml-1 text-xs text-muted-foreground">
                    ({col.type})
                  </span>
                </TableHead>
              ))}
              <TableHead className="w-[100px]">Created</TableHead>
              {isOwner && <TableHead className="w-[60px]">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {/* Add new row form */}
            {addingRow && (
              <TableRow className="bg-muted/50">
                {isOwner && <TableCell />}
                <TableCell className="font-mono text-xs text-muted-foreground">
                  (new)
                </TableCell>
                {editableColumns.map((col) => (
                  <TableCell key={col.name}>
                    <Input
                      value={newRowData[col.name] ?? ''}
                      onChange={(e) =>
                        setNewRowData({ ...newRowData, [col.name]: e.target.value })
                      }
                      placeholder={col.type}
                      className="h-7 text-sm"
                    />
                  </TableCell>
                ))}
                <TableCell />
                <TableCell>
                  <div className="flex items-center gap-1">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={handleSaveNewRow}
                          >
                            <Save className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Save row</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={handleCancelAddRow}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Cancel</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </TableCell>
              </TableRow>
            )}

            {/* Existing rows */}
            {rows.map((row) => (
              <TableRow key={row.id} className={selectedRows.has(row.id) ? 'bg-muted/50' : ''}>
                {isOwner && (
                  <TableCell>
                    <Checkbox
                      checked={selectedRows.has(row.id)}
                      onCheckedChange={() => toggleRowSelection(row.id)}
                    />
                  </TableCell>
                )}
                <TableCell className="font-mono text-xs">
                  {row.id.substring(0, 8)}...
                </TableCell>
                {editableColumns.map((col) => (
                  <TableCell key={col.name} className="max-w-[300px]">
                    {renderCell(row, col)}
                  </TableCell>
                ))}
                <TableCell className="text-xs text-muted-foreground">
                  {formatDate(row.createdAt)}
                </TableCell>
                {isOwner && (
                  <TableCell>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={() => {
                              setRowToDelete(row.id);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Delete row</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                )}
              </TableRow>
            ))}

            {rows.length === 0 && !addingRow && (
              <TableRow>
                <TableCell
                  colSpan={editableColumns.length + (isOwner ? 4 : 2)}
                  className="h-24 text-center text-muted-foreground"
                >
                  No rows yet. {isOwner && 'Click "Add Row" to add data.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Single Row Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Row</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this row? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteRow}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Dialog */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Selected Rows</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedRows.size} selected row(s)?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                `Delete ${selectedRows.size} Row(s)`
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
